import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Mail, CheckCircle2, Eye, MousePointerClick, AlertTriangle, Clock, XCircle, Send, Copy, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-600", Icon: Clock },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700", Icon: Send },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  delivery_delayed: { label: "Delayed", color: "bg-amber-100 text-amber-700", Icon: Clock },
  opened: { label: "Opened", color: "bg-violet-100 text-violet-700", Icon: Eye },
  clicked: { label: "Clicked", color: "bg-fuchsia-100 text-fuchsia-700", Icon: MousePointerClick },
  bounced: { label: "Bounced", color: "bg-rose-100 text-rose-700", Icon: XCircle },
  complained: { label: "Spam Report", color: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
  failed: { label: "Failed", color: "bg-rose-100 text-rose-800", Icon: XCircle },
  mocked: { label: "Mocked (dev)", color: "bg-slate-100 text-slate-500", Icon: AlertTriangle },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`} data-testid={`status-pill-${status}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function EmailLog() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [setupHidden, setSetupHidden] = useState(() => {
    try { return localStorage.getItem("email_log_setup_dismissed") === "1"; } catch { return false; }
  });

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await axios.get(`${API_URL}/emails`, { params });
      setEmails(res.data.emails || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load emails");
    }
    setLoading(false);
    setRefreshing(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so delivery statuses update without manual action
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = emails.filter((e) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (e.to || "").toLowerCase().includes(q) ||
      (e.subject || "").toLowerCase().includes(q) ||
      (e.entity_id || "").toLowerCase().includes(q)
    );
  });

  const counts = emails.reduce((acc, e) => { acc[e.status] = (acc[e.status] || 0) + 1; return acc; }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="email-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#0F4C81]" />
            Email Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">Live delivery status from Resend — refreshes every 30 seconds.</p>
        </div>
        <div className="flex items-center gap-2">
          {setupHidden && (
            <Button
              onClick={() => { try { localStorage.removeItem("email_log_setup_dismissed"); } catch {} setSetupHidden(false); }}
              variant="ghost"
              size="sm"
              className="text-amber-700 hover:bg-amber-50"
              data-testid="show-setup-guide"
            >
              Setup guide
            </Button>
          )}
          <Button onClick={() => load(true)} variant="outline" size="sm" disabled={refreshing} data-testid="email-log-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        {["sent", "delivered", "opened", "clicked", "bounced", "failed"].map((s) => {
          const meta = STATUS_META[s];
          const Icon = meta.Icon;
          return (
            <div key={s} className={`rounded-lg border p-3 ${meta.color.replace("text-", "border-").replace(/\d{3}/, "200")}`}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide`}>
                <Icon className="w-3.5 h-3.5" /> {meta.label}
              </div>
              <div className="mt-1 text-2xl font-bold text-slate-800">{counts[s] || 0}</div>
            </div>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <CardTitle className="text-base">Recent Emails</CardTitle>
            <div className="flex-1" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search recipient, subject, or entity id…"
              className="h-9 md:max-w-xs text-sm"
              data-testid="email-log-search"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 md:w-44 text-sm" data-testid="email-log-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="email-log-empty">
              <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No emails yet. Send your first quote via email and it will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">To</th>
                    <th className="text-left px-4 py-2 font-semibold">Subject</th>
                    <th className="text-left px-4 py-2 font-semibold">Linked</th>
                    <th className="text-left px-4 py-2 font-semibold">Sent</th>
                    <th className="text-left px-4 py-2 font-semibold">Opened</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`email-row-${e.id}`}>
                      <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{e.to}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-sm truncate" title={e.subject}>{e.subject}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {e.entity_type ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 font-mono">
                            {e.entity_type}:{(e.entity_id || "").slice(0, 8)}…
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(e.opened_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {!setupHidden && (
      <div className="mt-6 p-5 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl" data-testid="resend-webhook-setup">
        <div className="flex items-start justify-between mb-1">
          <p className="font-bold text-amber-900 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> One-time setup · 60 seconds</p>
          <button
            type="button"
            onClick={() => { try { localStorage.setItem("email_log_setup_dismissed", "1"); } catch {} setSetupHidden(true); }}
            className="text-xs text-amber-700 hover:text-amber-900 underline"
            data-testid="dismiss-setup"
          >
            Hide
          </button>
        </div>
        <p className="text-sm text-amber-900 mb-4">Follow these 2 steps once — then every email you send will show live Delivered/Opened/Clicked status here.</p>

        <div className="space-y-3">
          <div className="bg-white rounded-lg p-3 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
              <span className="text-sm font-semibold text-slate-800">Copy your webhook URL</span>
            </div>
            <div className="flex gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 font-mono text-xs text-slate-700 truncate" data-testid="webhook-url">
                {`${process.env.REACT_APP_BACKEND_URL}/api/webhook/resend`}
              </code>
              <Button
                size="sm"
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/resend`;
                  navigator.clipboard.writeText(url).then(() => toast.success("Webhook URL copied!"));
                }}
                className="h-9 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="copy-webhook-url"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copy
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
              <span className="text-sm font-semibold text-slate-800">Paste it in Resend</span>
            </div>
            <div className="flex items-start gap-3">
              <ol className="flex-1 text-xs text-slate-600 space-y-1 list-decimal list-inside">
                <li>Click the button to open Resend's Webhooks page in a new tab</li>
                <li>Click <span className="font-semibold">Add Endpoint</span>, paste the URL</li>
                <li>Under <span className="font-semibold">Events</span>, tick <span className="font-mono text-[11px] bg-slate-100 px-1 rounded">email.*</span> (selects all email events) — or manually tick Sent, Delivered, Bounced, Opened, Clicked, Complained</li>
                <li>Click <span className="font-semibold">Create</span>. Done.</li>
              </ol>
              <Button
                onClick={() => window.open("https://resend.com/webhooks", "_blank", "noopener")}
                className="h-9 bg-[#0F4C81] hover:bg-[#0d3f6c] text-white whitespace-nowrap"
                data-testid="open-resend-webhooks"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" /> Open Resend Webhooks
              </Button>
            </div>
          </div>
        </div>

        <p className="text-xs text-amber-800 mt-3 italic">Tip: once set up, send yourself a test quote — status should flip from <b>Sent</b> → <b>Delivered</b> within seconds, and to <b>Opened</b> the moment you click the email.</p>
      </div>
      )}
    </div>
  );
}
